/**
 * 入场分三档（2026-10-02 改造需求「访问状态分流」）：每次都看完整的心跳和爆炸，常来的时候会嫌慢。
 * - 完整（full）：这台设备第一次打开，或者在星海设置里点了「预览完整入场」。心跳 → 坍缩 → 爆炸 → 那段话（intro.ts 的 playIntro）
 * - 纪念日（anniversary）：今天是在一起的整周年、你们俩谁的生日，或者在一起的第 100、200…… 天，这台设备今天还没看过：
 *   极光升起，纪念日的话浮现，点一下就化成星尘飘走（playAnniversary）
 * - 日常（daily）：别的时候。星从中心的光点里绽开（0.8 秒），那段话在顶部停 2 秒后隐去，不挡操作（playDaily）
 * 看过哪一档按设备记在 localStorage 的 star-sea/intro。生日是成员自己填的（ADR-0011，MemberDto.birthday）。
 */
import { daysBetween, isLeapYear, monthDayIn, parseLocalDate, type LocalDate } from '@star-sea/shared';

export type IntroMode = 'full' | 'anniversary' | 'daily';

export const INTRO_MODES: readonly IntroMode[] = ['full', 'anniversary', 'daily'];

/** 这台设备看过什么 */
export interface IntroMemo {
  /** 看过完整入场 */
  full: boolean;
  /** 最近一次看纪念日入场是哪天 */
  anniv: LocalDate | null;
}

/** 今天是什么纪念日 */
export type Occasion =
  /** 第几周年；在一起的第几天 */
  | { kind: 'years' | 'days'; n: number }
  /** 谁的生日：mine 是自己的；both 是两个人同一天 */
  | { kind: 'birthday'; name: string; mine: boolean; both?: boolean };

/** 一个成员的生日（'MM-DD'）；mine 是这台设备上的自己 */
export interface Birthday {
  name: string;
  monthDay: string;
  mine: boolean;
}

/**
 * 今天是什么纪念日：在一起的整周年（2 月 29 日在一起的，平年算 2 月 28 日）、谁的生日（同样）、在一起的第 100、200…… 天
 * （在一起那天算第 1 天）；撞在同一天时按这个先后。都不是返回 null
 */
export function occasionOn(since: LocalDate, today: LocalDate, birthdays: readonly Birthday[] = []): Occasion | null {
  const s = parseLocalDate(since), t = parseLocalDate(today);
  const leapDay = s.m === 2 && s.d === 29 && !isLeapYear(t.y);
  if (t.y > s.y && t.m === s.m && t.d === (leapDay ? 28 : s.d)) return { kind: 'years', n: t.y - s.y };
  const born = birthdays.filter((b) => monthDayIn(b.monthDay, t.y) === today);
  if (born.length > 1) return { kind: 'birthday', name: '', mine: true, both: true };
  if (born[0]) return { kind: 'birthday', name: born[0].name, mine: born[0].mine };
  const day = daysBetween(since, today) + 1;
  if (day >= 100 && day % 100 === 0) return { kind: 'days', n: day };
  return null;
}

const NUMERALS = '一两三四五六七八九十';

/** 纪念日入场浮现的第一行：「在一起两周年」「在一起的第 500 天」 */
export function occasionText(o: Occasion): string {
  if (o.kind === 'birthday') return o.both ? '今天是你们俩的生日' : o.mine ? `${o.name}，生日快乐` : `今天是 ${o.name} 的生日`;
  if (o.kind === 'days') return `在一起的第 ${o.n.toLocaleString('zh-CN')} 天`;
  return o.n <= NUMERALS.length ? `在一起${NUMERALS.charAt(o.n - 1)}周年` : `在一起 ${o.n} 周年`;
}

/**
 * 这次打开放哪一档。forced 是网址里的 ?intro=full|anniversary|daily（调试用）或者「预览完整入场」；
 * 强制放纪念日入场、今天又不是纪念日时，拿一周年代替，免得没字可放。
 */
export function pickIntro(
  memo: IntroMemo | null,
  since: LocalDate,
  today: LocalDate,
  forced: IntroMode | null = null,
  birthdays: readonly Birthday[] = [],
): { mode: IntroMode; occasion: Occasion | null } {
  const occasion = occasionOn(since, today, birthdays);
  if (forced) return { mode: forced, occasion: forced === 'anniversary' ? (occasion ?? { kind: 'years', n: 1 }) : occasion };
  if (!memo?.full) return { mode: 'full', occasion };
  if (occasion && memo.anniv !== today) return { mode: 'anniversary', occasion };
  return { mode: 'daily', occasion };
}

/** 放完这一档以后记下什么：完整入场正好赶上纪念日的话，今天也算看过纪念日了 */
export function afterIntro(memo: IntroMemo | null, mode: IntroMode, occasion: Occasion | null, today: LocalDate): IntroMemo {
  const next: IntroMemo = { full: memo?.full ?? false, anniv: memo?.anniv ?? null };
  if (mode === 'full') next.full = true;
  if (occasion && mode !== 'daily') next.anniv = today;
  return next;
}

/** 从 localStorage 读出来的东西不一定可信（老版本、手改过）：认不出来就当没看过 */
export function parseMemo(raw: string | null): IntroMemo | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as unknown;
    if (!v || typeof v !== 'object') return null;
    const o = v as Record<string, unknown>;
    return { full: o.full === true, anniv: typeof o.anniv === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(o.anniv) ? (o.anniv as LocalDate) : null };
  } catch {
    return null;
  }
}
